import {
  addTemplate,
  checkTemplatesLimitsExceeded,
  copyTemplate,
  getBeeCentralizedEmailTemplates,
  getBeeMessageToken,
  getBeePluginConfig,
  getBeeTemplateDetails,
  getDynamicFieldsWhitelist,
  getTargetLists,
  getThirdPartyAccepted,
  getUsedTokens,
  setDynamicFieldsWhitelist,
  setThirdPartyResourceAlert,
  storeUsedTokens,
  updateTemplate
} from 'Pages/TemplateEdit/recoil/actions/actions'
import {
  beePluginState,
  editorState,
  openAIState
} from 'Pages/TemplateEdit/recoil/state/editor'
import {
  modalErrorState,
  modalOfferLightUpsellState,
  modalSaveState,
  modalThirdPartyState
} from 'Pages/TemplateEdit/recoil/state/modals'
import { pageErrorState, pageState } from 'Pages/TemplateEdit/recoil/state/page'

import {
  LOGIN_TYPE_WEBLOGIN,
  MSG_PLACEHOLDERS_SPECIAL_LINKS,
  STORE_KEY_LOGIN_TYPE
} from 'Constants/index'
import getIntl from 'Helpers/intl'
import { storeGet } from 'Helpers/localStorage'
import { logAppError } from 'Helpers/logger'
import { STORE_KEY_ID_LIST } from 'mailup-react-helpers'
import { toastState } from '../../../../recoil'

export const updateThirdPartyCallback = ({ set, reset }) => async () => {
  set(editorState, prev => ({
    ...prev,
    loading: true
  }))

  try {
    const loginType = storeGet(STORE_KEY_LOGIN_TYPE)
    if (loginType === LOGIN_TYPE_WEBLOGIN) {
      await setThirdPartyResourceAlert()
    }
    set(editorState, prev => ({
      ...prev,
      thirdPartyAccepted: true
    }))

    reset(modalThirdPartyState)
  } catch (error) {
    logAppError({
      appError: error
    })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }
  set(editorState, prev => ({
    ...prev,
    loading: false
  }))
}

export const modalSaveCallback = ({ set }) => async () => {
  set(pageState, prev => ({
    ...prev,
    loading: true
  }))

  try {
    const hasExceededTemplateLimits = await checkTemplatesLimitsExceeded()

    if (hasExceededTemplateLimits) {
      set(modalOfferLightUpsellState, { show: true })
    } else {
      const idList = storeGet(STORE_KEY_ID_LIST)
      const lists = await getTargetLists([idList])

      set(modalSaveState, prev => ({
        ...prev,
        show: true,
        saveToLists: [idList],
        lists
      }))
    }
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }

  set(pageState, prev => ({
    ...prev,
    loading: false
  }))
}

export const checkForThirdPartyCallback = ({ set }) => async () => {
  const thirdPartyAccepted = await getThirdPartyAccepted()

  set(editorState, prev => ({
    ...prev,
    thirdPartyAccepted
  }))

  set(modalThirdPartyState, prev => ({
    ...prev,
    show: !thirdPartyAccepted
  }))
}

export const saveTemplateCallback = ({ set, snapshot }) => async (json, cb) => {
  const { templateDetails, afterSaveCallback } = await snapshot.getPromise(
    editorState
  )
  const { actionModSaveRow } = await snapshot.getPromise(editorState)

  set(pageState, prev => ({
    ...prev,
    loading: true
  }))

  try {
    set(editorState, prev => ({
      ...prev,
      saved: true
    }))

    await updateTemplate(templateDetails.id, {
      Category: templateDetails.category,
      HasThumbnail: false,
      Structure: json,
      Thumbnail: templateDetails.thumbnail,
      Title: templateDetails.title,
      Type: templateDetails.type,
      Id: templateDetails.id
    })

    if (cb && typeof cb === 'function' && !actionModSaveRow) {
      const url = '/templates/bee'
      cb(url)
    }

    if (typeof afterSaveCallback === 'function') {
      afterSaveCallback({ id: templateDetails.id })
    }

    set(editorState, prev => ({
      ...prev,
      actionModSaveRow: false
    }))

    set(toastState, {
      message: getIntl().formatMessage({ id: 'Load.Bee.TemplateSaved' })
    })
  } catch (error) {
    logAppError({ appError: error })
    set(modalErrorState, {
      show: true
    })
    set(editorState, prev => ({
      ...prev,
      saved: false
    }))
  }

  set(pageState, prev => ({
    ...prev,
    loading: false
  }))
}

export const saveTemplateAsCallback = ({ set, snapshot, reset }) => async (
  json,
  cb
) => {
  const { title, lists, saveToLists, selectAll } = await snapshot.getPromise(
    modalSaveState
  )
  set(pageState, prev => ({
    ...prev,
    loading: true
  }))

  try {
    const idCurrentList = storeGet(STORE_KEY_ID_LIST)

    const { Id: newId } = await addTemplate({
      Structure: json,
      Title: title
    })

    const targetLists = selectAll
      ? lists.map(({ value }) => value)
      : saveToLists
    const filteredList = targetLists.filter(idList => idCurrentList !== idList)

    if (filteredList.length > 0) {
      await copyTemplate(newId, title, filteredList)
    }

    reset(modalSaveState)

    if (cb && typeof cb === 'function') {
      cb()
    }

    set(editorState, prev => ({
      ...prev,
      idNewTemplate: prev.templateDetails.mode !== 'copy' ? newId : 0,
      templateDetails: {
        ...prev.templateDetails,
        title
      }
    }))

    set(toastState, {
      message: getIntl().formatMessage({ id: 'Load.Bee.TemplateSaved' })
    })
  } catch (error) {
    logAppError({ appError: error })
    set(modalErrorState, {
      show: true
    })
  }

  set(pageState, prev => ({
    ...prev,
    loading: false
  }))
}

export const initBeePluginTemplatesCallback = ({ set, snapshot }) => async ({
  mode,
  id,
  settings
}) => {
  set(pageState, prev => ({
    ...prev,
    loading: true
  }))

  const onLoad = () => {
    set(beePluginState, prev => ({ ...prev, loaded: true }))
  }

  const onError = err => {
    console.error(err)
    logAppError({
      appError: err,
      Component: 'TemplateEdit',
      Method: 'BeePluginEditor',
      Message: 'Error triggered by Bee Plugin'
    })
  }

  try {
    const token = await getBeeMessageToken()

    const config = await getBeePluginConfig()

    const dynamicFieldsWhitelist = await getDynamicFieldsWhitelist('TPL', id)

    const {
      Category: category,
      HasThumbnail: hasThumbnail,
      Thumbnail: thumbnail,
      Title: title,
      Type: type,
      Structure: structureString
    } = await (mode === 'copy'
      ? getBeeCentralizedEmailTemplates(id)
      : getBeeTemplateDetails(id))

    const templateDetails = {
      category,
      hasThumbnail,
      id,
      mode,
      thumbnail,
      title,
      type,
      structure: JSON.parse(structureString)
    }

    set(editorState, prev => ({
      ...prev,
      templateDetails,
      mergeTags: [
        ...config.mergeTags.map(({ name }) => name),
        ...MSG_PLACEHOLDERS_SPECIAL_LINKS
      ],
      mergeTagsWhitelist: dynamicFieldsWhitelist
    }))

    set(pageState, prev => ({
      ...prev,
      loading: false,
      title
    }))

    const beeConfig = {
      ...config,
      onLoad,
      onError,
      ...settings
    }

    window.BeePlugin.create(token, beeConfig, instance => {
      set(beePluginState, prev => ({ ...prev, instance }))
      instance.start(templateDetails.structure)
    })
  } catch (error) {
    logAppError({
      appError: error
    })
    set(pageErrorState, {
      error: getIntl().formatMessage({
        id: 'Load.Bee.EditorInitializationError'
      }),
      severity: 'danger'
    })
    set(pageState, prev => ({
      ...prev,
      loading: false
    }))
  }
}

export const addMergeTagToWhitelistCallback = ({
  set,
  snapshot
}) => async tag => {
  const { mergeTagsWhitelist, templateDetails } = await snapshot.getPromise(
    editorState
  )
  const newMergeTagsWhitelist = new Set(mergeTagsWhitelist).add(tag)

  try {
    setDynamicFieldsWhitelist('TPL', templateDetails.id, [
      ...newMergeTagsWhitelist
    ])
    set(editorState, prev => ({
      ...prev,
      mergeTagsWhitelist: [...newMergeTagsWhitelist]
    }))
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }
}

export const storeUsedTokensCounterCallback = () => async value => {
  try {
    await storeUsedTokens(value)
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'MessageEdit',
      Method: 'storeUsedTokensCounterCallback'
    })
  }
}

export const getTotalTokensCountCallback = ({ set }) => async () => {
  try {
    const resp = await getUsedTokens()
    set(openAIState, prev => ({
      ...prev,
      totalTokens: resp.TotalTokens,
      remainingTokens: resp.RemainingTokens,
      enableUse: resp.EnableUse,
      showBuy: resp.ShowBuy,
      loaded: true
    }))
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'MessageEdit',
      Method: 'getTotalTokensCountCallback'
    })
  }
}
